import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Collapse,
  Link,
  Typography,
  Alert,
} from '@mui/material';
import { horsesAPI } from '../services/api';
import PedigreeTable from './PedigreeTable';
import AllBreedCandidateDialog from './AllBreedCandidateDialog';
import { toArray } from '../utils/apiData';

// One attempt allows 30s to load the page plus a best-effort 10s for the chart,
// plus browser startup. Quoted to the user so a long wait doesn't look like a hang.
const ATTEMPT_SECONDS = 45;
const RETRY_BACKOFF_SECONDS = 3;
const MAX_ATTEMPTS = 2;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// What each fetch outcome means to the person who pressed the button. Keys are
// the statuses returned by POST /api/horses/<id>/pedigree/fetch.
const FETCH_MESSAGES = {
  none: {
    severity: 'warning',
    text: 'No matching horse found on AllBreed. You can paste the pedigree HTML manually from the admin section.',
  },
  empty: {
    severity: 'warning',
    text: 'AllBreed has no pedigree chart under that name. It may be listed '
      + 'differently there, or not listed at all. Nothing was saved.',
  },
  unsupported: {
    severity: 'warning',
    text: 'AllBreed returned a chart that was not 5 generations deep, so it was not saved.',
  },
};

const PedigreeModal = ({ open, onClose, horseId, horseName }) => {
  const [pedigreeData, setPedigreeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [horseType, setHorseType] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchMessage, setFetchMessage] = useState(null);
  const [candidates, setCandidates] = useState(null);
  // Progress for a fetch that can legitimately run for a minute and a half.
  const [attempt, setAttempt] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [retryIn, setRetryIn] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  // Lets an in-flight retry loop abandon itself when the modal closes.
  const cancelled = useRef(false);

  // Ticks the elapsed counter for whichever attempt is running. Keyed on
  // `attempt` so each attempt's timer restarts from zero.
  useEffect(() => {
    if (!fetching || retryIn > 0) return undefined;
    setElapsed(0);
    const started = Date.now();
    const id = setInterval(
      () => setElapsed(Math.round((Date.now() - started) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [fetching, attempt, retryIn]);

  // Counts the backoff down so the pause between attempts is visibly deliberate
  // rather than looking like a stall.
  useEffect(() => {
    if (retryIn <= 0) return undefined;
    const id = setInterval(() => setRetryIn((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [retryIn]);

  const loadHorseData = useCallback(async () => {
    try {
      const response = await horsesAPI.getById(horseId);
      const horse = response.data;
      setHorseType(horse.type?.name?.toLowerCase() || '');
    } catch (err) {
      console.error('Error loading horse data:', err);
    }
  }, [horseId]);

  const loadPedigree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await horsesAPI.getPedigreeDisplay(horseId);
      setPedigreeData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No pedigree data found for this horse. Please add pedigree data in the admin section.');
      } else {
        setError(err.response?.data?.error || 'Failed to load pedigree');
      }
    } finally {
      setLoading(false);
    }
  }, [horseId]);

  // Turn a failed response into what to show and whether it is worth retrying.
  // Only a timeout retries: "blocked" means AllBreed is already refusing us, and
  // retrying that earns a longer block rather than a pedigree.
  const describeFailure = useCallback((err) => {
    if (!err.response) {
      return {
        retryable: false,
        message: {
          severity: 'error',
          text: 'The request never completed — the server may still be working, or '
            + 'the connection dropped. Check that the backend is running.',
          detail: err.message,
        },
      };
    }
    const data = err.response.data || {};
    return {
      retryable: data.reason === 'timeout',
      message: {
        severity: 'error',
        text: data.error || 'The fetch from AllBreed failed.',
        detail: data.detail,
        slug: data.slug,
        url: data.url,
      },
    };
  }, []);

  // slug is set only when answering a "multiple" result — otherwise the backend
  // resolves the horse itself.
  const handleFetch = useCallback(async (slug) => {
    cancelled.current = false;
    setFetching(true);
    setFetchMessage(null);
    setCandidates(null);
    setShowDetail(false);

    try {
      for (let n = 1; n <= MAX_ATTEMPTS; n += 1) {
        setAttempt(n);
        try {
          const response = await horsesAPI.fetchPedigreeFromAllbreed(
            horseId, slug ? { slug } : {},
          );
          const { status } = response.data;

          if (status === 'fetched') {
            await loadPedigree();
          } else if (status === 'multiple') {
            setCandidates(toArray(response.data?.candidates, 'pedigree candidates'));
          } else if (status === 'mismatch') {
            // The slug was a guess and landed on a different horse. Say which one
            // — that is the detail that lets the user judge — and offer the picker
            // when there are alternatives to choose from.
            setFetchMessage({
              severity: 'warning',
              text: `AllBreed returned "${response.data.found}" for this search, not `
                + `"${response.data.expected}", so nothing was saved.`,
            });
            if (response.data.candidates?.length) {
              setCandidates(toArray(response.data?.candidates, 'pedigree candidates'));
            }
          } else {
            // Carry the slug and URL through so "Show details" answers the
            // obvious next question: what exactly did it look for?
            setFetchMessage({
              ...(FETCH_MESSAGES[status] || {
                severity: 'warning',
                text: `AllBreed fetch finished with an unexpected result (${status}).`,
              }),
              slug: response.data.slug,
              url: response.data.url,
            });
          }
          return;
        } catch (err) {
          // 409 means a pedigree turned up between opening the modal and pressing
          // the button — show it rather than reporting a failure.
          if (err.response?.status === 409) {
            await loadPedigree();
            return;
          }

          const { retryable, message } = describeFailure(err);
          if (!retryable || n === MAX_ATTEMPTS || cancelled.current) {
            setFetchMessage(message);
            return;
          }

          setRetryIn(RETRY_BACKOFF_SECONDS);
          await sleep(RETRY_BACKOFF_SECONDS * 1000);
          setRetryIn(0);
          if (cancelled.current) return;
        }
      }
    } finally {
      setFetching(false);
      setAttempt(0);
      setRetryIn(0);
    }
  }, [horseId, loadPedigree, describeFailure]);

  useEffect(() => {
    if (open && horseId) {
      loadHorseData();
      loadPedigree();
    } else {
      // Abandon any retry loop still sleeping between attempts.
      cancelled.current = true;
      setPedigreeData(null);
      setError(null);
      setHorseType(null);
      setFetchMessage(null);
      setCandidates(null);
      setShowDetail(false);
    }
  }, [open, horseId, loadHorseData, loadPedigree]);

  const hasRows = Boolean(pedigreeData?.rows?.length);
  const showEmptyState = !loading && !error && !hasRows && !pedigreeData?.error;

  const fetchButton = (
    <Button
      variant="outlined"
      size="small"
      onClick={() => handleFetch(null)}
      disabled={fetching}
      startIcon={fetching ? <CircularProgress size={16} /> : null}
    >
      {fetching ? 'Fetching from AllBreed…' : 'Fetch from AllBreed'}
    </Button>
  );

  // What the fetch is doing right now. A fetch can legitimately take a minute and
  // a half across two attempts, so silence would read as a hang.
  const progress = (() => {
    if (!fetching) return null;
    if (retryIn > 0) {
      return {
        line: `AllBreed timed out. Retrying in ${retryIn}…`,
        note: `Attempt ${attempt} of ${MAX_ATTEMPTS} did not answer. One more try.`,
      };
    }
    return {
      line: attempt > 1
        ? `Retrying (attempt ${attempt} of ${MAX_ATTEMPTS})… ${elapsed}s`
        : `Contacting AllBreed… ${elapsed}s`,
      note: `Usually a few seconds; each attempt gives up at about ${ATTEMPT_SECONDS}s.`,
    };
  })();

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '95vh',
          height: 'auto',
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, pt: 1, px: 2 }}>
        Pedigree:
      </DialogTitle>
      <DialogContent sx={{ p: 0.5, px: 1, '&.MuiDialogContent-root': { paddingTop: 0.5 } }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 0.5 }}>
            <Typography color="error" variant="body2">{error}</Typography>
          </Box>
        )}

        {!loading && !error && hasRows && (
          <Box sx={{ maxHeight: '85vh', overflow: 'auto' }}>
            <PedigreeTable data={pedigreeData} horseType={horseType} />
          </Box>
        )}

        {!loading && !error && pedigreeData?.error && (
          <Box sx={{ p: 0.5 }}>
            <Alert severity="info">{pedigreeData.error}</Alert>
          </Box>
        )}

        {showEmptyState && (
          <Box sx={{ p: 1 }}>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              No pedigree stored for this horse.
            </Alert>
            {fetchButton}
            {progress && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {progress.line}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {progress.note}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {fetchMessage && (
          <Box sx={{ px: 1, pb: 1 }}>
            <Alert severity={fetchMessage.severity}>
              {fetchMessage.text}
              {(fetchMessage.detail || fetchMessage.slug) && (
                <>
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    underline="hover"
                    onClick={() => setShowDetail((v) => !v)}
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {showDetail ? 'Hide details' : 'Show details'}
                  </Link>
                  <Collapse in={showDetail}>
                    <Box
                      sx={{
                        mt: 0.5,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {fetchMessage.slug && <div>slug: {fetchMessage.slug}</div>}
                      {fetchMessage.url && <div>url: {fetchMessage.url}</div>}
                      {fetchMessage.detail && <div>{fetchMessage.detail}</div>}
                    </Box>
                  </Collapse>
                </>
              )}
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 0.5 }}>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>

    {/* Sibling rather than a child, so it stacks above the pedigree dialog
        instead of inheriting its layer. */}
    <AllBreedCandidateDialog
      open={Boolean(candidates)}
      title={`Several AllBreed horses match ${horseName || 'this horse'}`}
      candidates={candidates || []}
      onSelect={(candidate) => handleFetch(candidate.slug)}
      onClose={() => setCandidates(null)}
    />
    </>
  );
};

export default PedigreeModal;
